import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSite } from "../../lib/useSite";
import { api, apiError } from "../../lib/api";
import { Eyebrow } from "../../components/bits";
import { ArrowRight, ArrowLeft, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Your details", "Property", "Service", "Preferences"];
const PROP_TYPES = ["Flat / Apartment", "House", "Office", "Commercial premises", "Other"];
const FREQ = ["One-off", "Weekly", "Fortnightly", "Monthly", "Custom"];
const COND = ["Excellent", "Average", "Needs attention", "Post-renovation"];

const field = "w-full bg-white border border-[#e0ddd4] rounded-sm px-4 py-3 text-sm focus:border-[#d4af37]";
const labelCls = "text-xs tracking-wide text-[#555] uppercase mb-1.5 block";

export default function GetQuote() {
  const site = useSite();
  const services = site?.services || [];
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", phone: "", property_type: "", postcode: "",
    bedrooms: "", bathrooms: "", size_sqm: "", service: "", frequency: "One-off",
    preferred_date: "", preferred_time: "", access_info: "", pets: "", parking: "",
    condition: "", add_ons: [], notes: "", photos: [],
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isCommercial = ["Office", "Commercial premises"].includes(f.property_type);
  const serviceObj = services.find((s) => s.name === f.service);

  const toggleAddon = (a) => set("add_ons", f.add_ons.includes(a) ? f.add_ons.filter((x) => x !== a) : [...f.add_ons, a]);

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    const arr = await Promise.all(files.map((file) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file);
    })));
    set("photos", [...f.photos, ...arr].slice(0, 4));
  };

  const canNext = () => {
    if (step === 0) return f.name && /.+@.+\..+/.test(f.email);
    if (step === 1) return f.property_type && f.postcode;
    if (step === 2) return f.service;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        bedrooms: f.bedrooms ? Number(f.bedrooms) : null,
        bathrooms: f.bathrooms ? Number(f.bathrooms) : null,
        size_sqm: f.size_sqm ? Number(f.size_sqm) : null,
        source: "Website Quote",
      };
      const { data } = await api.post("/public/enquiry", payload);
      setDone(data);
      toast.success("Quote request received");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not submit");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center">
        <CheckCircle2 className="text-[#d4af37] mx-auto" size={54} />
        <h1 className="font-display text-4xl tracking-tight mt-6">Thank you, {f.name.split(" ")[0]}</h1>
        <p className="text-[#555] mt-3">Your request <span className="font-semibold">{done.reference}</span> has been received and logged with our team.</p>
        <div className="card-luxe p-8 mt-8 text-left">
          {done.requires_manual ? (
            <>
              <p className="text-sm font-semibold">This job requires a written quotation.</p>
              <p className="text-sm text-[#555] mt-2">A member of our team will review the details and send you a formal, no-obligation quote — no binding price is generated automatically for jobs needing review.</p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold">Indicative estimate</p>
              <p className="font-display text-3xl mt-2 text-[#0a0a0a]">£{done.estimate_min} – £{done.estimate_max}</p>
              <p className="text-xs text-[#8a8a8a] mt-2">This is a non-binding indicative estimate. A confirmed written quotation will follow.</p>
            </>
          )}
        </div>
        <p className="text-sm text-[#8a8a8a] mt-6">You can create a customer account to track and accept your quotation once it's ready.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#fdfbf7]">
      <section className="bg-[#0a0a0a] relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-12">
          <Eyebrow dark>Get a Quote</Eyebrow>
          <h1 className="font-display text-[#fdfbf7] text-4xl sm:text-5xl tracking-tight">Request your quotation</h1>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? "bg-[#d4af37]" : "bg-[#e0ddd4]"}`} />
              <p className={`text-[11px] mt-2 ${i === step ? "text-[#0a0a0a] font-semibold" : "text-[#8a8a8a]"}`}>{s}</p>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2"><label className={labelCls}>Full name *</label><input data-testid="q-name" className={field} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
                <div><label className={labelCls}>Email *</label><input data-testid="q-email" type="email" className={field} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
                <div><label className={labelCls}>Mobile</label><input data-testid="q-phone" className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              </div>
            )}
            {step === 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div><label className={labelCls}>Property / business type *</label>
                  <select data-testid="q-proptype" className={field} value={f.property_type} onChange={(e) => set("property_type", e.target.value)}>
                    <option value="">Select…</option>{PROP_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select></div>
                <div><label className={labelCls}>Postcode *</label><input data-testid="q-postcode" className={field} value={f.postcode} onChange={(e) => set("postcode", e.target.value)} /></div>
                {!isCommercial && <>
                  <div><label className={labelCls}>Bedrooms</label><input type="number" min="0" className={field} value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></div>
                  <div><label className={labelCls}>Bathrooms</label><input type="number" min="0" className={field} value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} /></div>
                </>}
                <div className="sm:col-span-2"><label className={labelCls}>Approx. size (m², if known)</label><input type="number" className={field} value={f.size_sqm} onChange={(e) => set("size_sqm", e.target.value)} /></div>
              </div>
            )}
            {step === 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div><label className={labelCls}>Service required *</label>
                  <select data-testid="q-service" className={field} value={f.service} onChange={(e) => set("service", e.target.value)}>
                    <option value="">Select…</option>{services.map((s) => <option key={s.id}>{s.name}</option>)}
                  </select></div>
                <div><label className={labelCls}>Frequency</label>
                  <select className={field} value={f.frequency} onChange={(e) => set("frequency", e.target.value)}>{FREQ.map((x) => <option key={x}>{x}</option>)}</select></div>
                <div><label className={labelCls}>Cleaning condition</label>
                  <select className={field} value={f.condition} onChange={(e) => set("condition", e.target.value)}><option value="">Select…</option>{COND.map((x) => <option key={x}>{x}</option>)}</select></div>
                {serviceObj?.add_ons?.length > 0 && (
                  <div className="sm:col-span-2"><label className={labelCls}>Add-ons</label>
                    <div className="flex flex-wrap gap-2">
                      {serviceObj.add_ons.map((a) => (
                        <button type="button" key={a} onClick={() => toggleAddon(a)}
                          className={`text-sm px-3 py-1.5 rounded-sm border ${f.add_ons.includes(a) ? "bg-[#d4af37] text-[#0a0a0a] border-[#d4af37]" : "border-[#e0ddd4] text-[#555]"}`}>{a}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {step === 3 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div><label className={labelCls}>Preferred date</label><input type="date" className={field} value={f.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} /></div>
                <div><label className={labelCls}>Preferred time</label><input className={field} placeholder="e.g. Morning" value={f.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} /></div>
                <div><label className={labelCls}>Access information</label><input className={field} value={f.access_info} onChange={(e) => set("access_info", e.target.value)} /></div>
                <div><label className={labelCls}>Parking</label><input className={field} value={f.parking} onChange={(e) => set("parking", e.target.value)} /></div>
                <div><label className={labelCls}>Pets</label><input className={field} value={f.pets} onChange={(e) => set("pets", e.target.value)} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Special requirements / notes</label><textarea rows="3" className={field} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Upload photographs (optional)</label>
                  <label className="flex items-center gap-2 border border-dashed border-[#cfcabf] rounded-sm px-4 py-4 cursor-pointer text-sm text-[#555]">
                    <Upload size={16} /> Choose images
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
                  </label>
                  {f.photos.length > 0 && <div className="flex gap-2 mt-3">{f.photos.map((p, i) => <img key={i} src={p} alt="" className="w-16 h-16 object-cover rounded-sm border border-[#e0ddd4]" />)}</div>}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-10">
          <button data-testid="q-back" disabled={step === 0} onClick={() => setStep((s) => s - 1)}
            className={`flex items-center gap-2 px-5 py-3 rounded-sm text-sm ${step === 0 ? "opacity-0 pointer-events-none" : "btn-outline-gold"}`}>
            <ArrowLeft size={16} /> Back
          </button>
          {step < 3 ? (
            <button data-testid="q-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}
              className={`flex items-center gap-2 px-6 py-3 rounded-sm text-sm ${canNext() ? "btn-gold" : "bg-[#e0ddd4] text-[#999]"}`}>
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="q-submit" disabled={busy} onClick={submit} className="btn-gold px-8 py-3 rounded-sm text-sm">
              {busy ? "Submitting…" : "Submit request"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
